import { Button, Kbd } from "@clockwise/design-system";
import { FindTime } from "@clockwise/design-system/icons";
import {
  INPUT_BACKGROUND_CLASS,
  INPUT_BORDER_RADIUS_CLASS,
  INPUT_BORDER_SHADOW_CLASS,
  INPUT_HEIGHT_CLASS,
  INPUT_MIN_WIDTH_CLASS,
  INPUT_PADDING_CLASS,
  INPUT_TEXT_CLASS,
} from "@clockwise/design-system/src/constants/classes";
import { fg_muted } from "@clockwise/design-system/tokens";
import { RightPanelCloseFilled } from "@clockwise/icons";
import { EcosystemEnum } from "@clockwise/schema";
import { CalendarDensity, CalendarMode } from "@clockwise/web-commons/src/components/calendar";
import { CalendarMenu } from "@clockwise/web-commons/src/components/calendar/calenar-menu/CalendarMenu";
import { CalendarModeToggle } from "@clockwise/web-commons/src/components/calendar/calendar-mode/CalendarMode";
import {
  CalendarStepNavigation,
  HighlightedNavButton,
} from "@clockwise/web-commons/src/components/calendar/calendar-step-navigation/CalendarStepNavigation";
import { getRenderTimeZone } from "@clockwise/web-commons/src/util/time-zone.util";
import { DatePicker } from "@material-ui/pickers";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import classNames from "classnames";
import { DateTime } from "luxon";
import React, { useMemo } from "react";
import { useFeatureFlag } from "../../../launch-darkly/useLaunchDarkly";
import { CalendarIds } from "../../../state/actions/multi-calendar.actions";
import { aiSchedulerRoadmapURL, changelogURL } from "../../chat/constants";
import { PrismButton } from "../../prism/Prism";
import { MultiCalendarSelect } from "../multi-calendar-select/";

const MutedFindTime = () => <FindTime fill={fg_muted} />;

type Props = {
  calendarPeek?: boolean;
  dateFormat: "MMM YYYY" | "MMMM YYYY" | "ddd, MMMM DD YYYY" | "MMM DD YYYY";
  currentDate: string;
  declinedEventsEnabled: boolean;
  density: CalendarDensity;
  ecosystem: EcosystemEnum;
  feedbackURL: string;
  loading: boolean;
  mode: CalendarMode;
  onCalendarDensityChange: (density: CalendarDensity) => void;
  onCalendarModeChange: (view: CalendarMode) => void;
  onFadePastEventsChange: (fadePastEvents: boolean) => void;
  onFindTime?: () => void;
  onMultiCalAdd: (calendarIds: string[]) => void;
  onMultiCalRemove: (calendarIds: string[]) => void;
  onNavigateToDate: (date: string) => void;
  onSchedule?: (roomRequired: boolean) => void;
  onShiftNext: () => void;
  onShiftPrevious: () => void;
  onShowDeclinedEventsChange: (shown: boolean) => void;
  onShowWeekendsChange: (shown: boolean) => void;
  onTodayClick: () => void;
  onExpandChat: () => void;
  chatIsCollapsed: boolean;
  previewedCalendarId: CalendarIds;
  selectedCalendarIds: CalendarIds;
  weekendsEnabled: boolean;
  fadePastEvents: boolean;
  navButtonToHighlight: HighlightedNavButton;
  highlightedNavButtonTooltip:
    | (({ anchorEl }: { anchorEl: React.RefObject<HTMLDivElement> }) => React.JSX.Element)
    | null;
};

const LONG_FORM_WIDTH_ADDITION = 50;
export const PlannerToolbarRender = ({
  calendarPeek = false,
  currentDate,
  dateFormat,
  declinedEventsEnabled,
  density,
  feedbackURL,
  mode,
  onCalendarDensityChange,
  onCalendarModeChange,
  onFadePastEventsChange,
  onFindTime,
  onMultiCalAdd,
  onMultiCalRemove,
  onNavigateToDate,
  onShiftNext,
  onShiftPrevious,
  onShowDeclinedEventsChange,
  onShowWeekendsChange,
  onTodayClick,
  onExpandChat,
  chatIsCollapsed,
  previewedCalendarId,
  selectedCalendarIds,
  weekendsEnabled,
  fadePastEvents,
  navButtonToHighlight,
  highlightedNavButtonTooltip,
}: Props) => {
  const [hasPrismComponents] = useFeatureFlag("Prism-Iridescence");
  const [enableCollapsibleChat] = useFeatureFlag("CollapsibleSidebar");
  const [enableCommandBar] = useFeatureFlag("CommandBar");
  const showLongForm = dateFormat === "ddd, MMMM DD YYYY";

  const datePickerWidth = useMemo(() => {
    // DevNote: remove when we upgrate off of material-ui-pickers
    //    date picker lib does not support non-input element;
    //    inputs dont flex width with content so;
    //    we need to set a width based on the month.
    const dateParts = currentDate.split("-");
    const month = dateParts[1];

    switch (month) {
      // x-long months
      case "09": // September
      case "11": // November
      case "12": // December
        return 130;

      // long months
      case "01": // January
      case "02": // Feburary
      case "10": // October
        return 110;

      // mid-length months
      case "03": // March
      case "08": // August
        return 100;

      // short months
      case "04": // April
      case "05": // May
      case "06": // June
      case "07": // July
        return 90;

      // edge case
      default:
        // Undecimber
        return 140;
    }
  }, [currentDate]);

  const currentJSDate = DateTime.fromISO(currentDate, { zone: getRenderTimeZone() }).toJSDate();

  const handleNavigateToDate = (dateMoment: MaterialUiPickersDate) => {
    if (!dateMoment) {
      return;
    }
    return onNavigateToDate(DateTime.fromISO(dateMoment.toISOString()).toISODate());
  };

  return (
    <div className="cw-w-full cw-px-3 cw-mt-1.5 cw-mb-3 cw-flex cw-flex-row cw-items-center cw-justify-between cw-z-[51] cw-sticky">
      <div className="cw-flex cw-flex-row cw-items-center cw-gap-2 xs:cw-gap-2 cw-flex-wrap">
        {enableCollapsibleChat && chatIsCollapsed && (
          <Button
            onClick={onExpandChat}
            aria-label="Expand chat sidebar"
            variant="outlined"
            size="xsmall"
          >
            <RightPanelCloseFilled className="cw-text-muted cw-w-4 cw-h-4" />
          </Button>
        )}
        {!!onFindTime &&
          (hasPrismComponents ? (
            <PrismButton
              onClick={onFindTime}
              size="xsmall"
              startIcon={MutedFindTime}
              variant="outlined"
            >
              Find time
              {enableCommandBar && (
                <span className="cw-text-muted cw-flex cw-gap-[2px] cw-h-[18px] cw-leading-[18px]">
                  <Kbd size="xs" className="cw-leading-[11px]">
                    ⌘
                  </Kbd>
                  <Kbd size="xs" className="cw-leading-[11px]">
                    K
                  </Kbd>
                </span>
              )}
            </PrismButton>
          ) : (
            <Button
              onClick={onFindTime}
              sentiment="neutral"
              size="xsmall"
              startIcon={FindTime}
              variant="outlined"
            >
              Find time
            </Button>
          ))}
        <CalendarStepNavigation
          mode={mode}
          onPrevious={onShiftPrevious}
          onToday={onTodayClick}
          onNext={onShiftNext}
          highlightedButton={navButtonToHighlight}
          highlightedTooltip={highlightedNavButtonTooltip}
        />
        <DatePicker
          id="day-date-picker"
          onChange={handleNavigateToDate}
          value={currentJSDate}
          variant="inline"
          autoOk
          disableToolbar
          format={dateFormat}
          InputProps={{
            disableUnderline: true,
            classes: {
              input: classNames(
                INPUT_TEXT_CLASS["small"],
                INPUT_HEIGHT_CLASS["small"],
                INPUT_MIN_WIDTH_CLASS["small"],
                INPUT_BORDER_RADIUS_CLASS["small"],
                INPUT_BORDER_SHADOW_CLASS["default"],
                INPUT_BACKGROUND_CLASS["default"]["neutral"],
                INPUT_PADDING_CLASS["default"]["mini"],
                "cw-cursor-pointer",
                "cw-text-center",
              ),
            },
            style: {
              width: showLongForm ? datePickerWidth + LONG_FORM_WIDTH_ADDITION : datePickerWidth,
            },
            label: "Current Month",
            tabIndex: -1,
            role: "presentation",
          }}
          onClick={() => {
            // Click the keyboard icon to open the date picker.
            const changeDateIcon = document.querySelector(
              "[aria-label='change date']",
            ) as HTMLElement;
            if (changeDateIcon) {
              changeDateIcon.click?.();
            }
          }}
        />
      </div>
      <div className="cw-flex cw-items-center cw-space-x-2">
        {!!calendarPeek && (
          <MultiCalendarSelect
            onMultiCalAdd={onMultiCalAdd}
            onMultiCalRemove={onMultiCalRemove}
            previewedCalendarId={previewedCalendarId}
            selectedCalendarIds={selectedCalendarIds}
          />
        )}
        <CalendarModeToggle calendarMode={mode} onCalendarModeChange={onCalendarModeChange} />

        <CalendarMenu
          declinedEvents={declinedEventsEnabled}
          density={density}
          onCalendarDensityChange={onCalendarDensityChange}
          onShowDeclinedEventsChange={onShowDeclinedEventsChange}
          onShowWeekendsChange={onShowWeekendsChange}
          onFadePastEventsChange={onFadePastEventsChange}
          weekends={weekendsEnabled}
          fadePastEvents={fadePastEvents}
          changelogUrl={changelogURL}
          feedbackURL={feedbackURL}
          productRoadmapUrl={aiSchedulerRoadmapURL}
        />
      </div>
    </div>
  );
};

export default PlannerToolbarRender;
